import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { SubscriptionBanner } from '@/components/SubscriptionBanner';
import { SubscriptionLock } from '@/components/SubscriptionLock';
import { SubscriptionGate } from '@/components/SubscriptionGate';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Bot, Zap, BookOpen, Rocket, BarChart3, Settings, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getStripeEnvironment } from '@/lib/stripe';
import { toast } from 'sonner';

export default function CustomerPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currentTier, tierLabel, subscription, isActive } = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);

  const openBillingPortal = async () => {
    if (!user) {
      navigate('/auth?redirect=/portal');
      return;
    }
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-billing-portal-session', {
        body: {
          environment: getStripeEnvironment(),
          returnUrl: `${window.location.origin}/portal`,
        },
      });
      if (error || !data?.url) {
        throw new Error(error?.message || data?.error || 'Failed to open billing portal');
      }
      window.open(data.url, '_blank');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const periodEnd = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString()
    : null;

  return (
    <>
      <SEO
        title="Customer Portal - Neil AI Global Solutions"
        description="Access your AI agents, automation workflows, and enterprise tools in your Neil AI customer portal."
      />
      <div className="min-h-screen bg-gradient-to-b from-background via-background/95 to-background/90">
        <Navigation />
        <SubscriptionBanner />
        <SubscriptionLock />

        <main className="container mx-auto px-4 py-12 pt-44 sm:pt-48">
          <div className="max-w-6xl mx-auto space-y-8">
            {/* Welcome */}
            <div className="text-center space-y-4 mb-12">
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground">
                Welcome to Your Neil AI Automation Hub
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Launch and manage your AI agents, funnels and automation tools from one place.
              </p>
            </div>

            {/* Subscription summary card */}
            {user && currentTier !== 'none' && (
              <Card>
                <CardHeader>
                  <CardTitle>Your Plan</CardTitle>
                  <CardDescription>
                    {tierLabel} {isActive ? '— Active' : ''}
                    {subscription?.cancel_at_period_end && periodEnd
                      ? ` (cancels on ${periodEnd})`
                      : periodEnd
                        ? ` · renews ${periodEnd}`
                        : ''}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-3">
                  <Button onClick={openBillingPortal} disabled={portalLoading}>
                    {portalLoading ? 'Opening…' : 'Manage Billing'}
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/pricing')}>
                    Change Plan
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Starter Tools */}
            <SubscriptionGate requiredTier="essential">
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-foreground">Starter Tools</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader>
                      <Bot className="w-10 h-10 text-primary mb-2" />
                      <CardTitle>Voice AI Agent Control Panel</CardTitle>
                      <CardDescription>Configure and monitor your AI voice agents</CardDescription>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader>
                      <Zap className="w-10 h-10 text-primary mb-2" />
                      <CardTitle>Basic Automation Workflows</CardTitle>
                      <CardDescription>Set up automated tasks and triggers</CardDescription>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader>
                      <BookOpen className="w-10 h-10 text-primary mb-2" />
                      <CardTitle>Onboarding & Training Library</CardTitle>
                      <CardDescription>Access guides and video tutorials</CardDescription>
                    </CardHeader>
                  </Card>
                </div>
              </section>
            </SubscriptionGate>

            {/* Growth & Enterprise Tools */}
            <SubscriptionGate requiredTier="growth">
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-foreground">Growth & Enterprise Tools</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader>
                      <Rocket className="w-10 h-10 text-primary mb-2" />
                      <CardTitle>AI Funnel Builder</CardTitle>
                      <CardDescription>Create custom conversion funnels</CardDescription>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader>
                      <Zap className="w-10 h-10 text-primary mb-2" />
                      <CardTitle>Social Campaign Accelerator</CardTitle>
                      <CardDescription>Automate your social media campaigns</CardDescription>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader>
                      <Settings className="w-10 h-10 text-primary mb-2" />
                      <CardTitle>Custom Domain AI Portal</CardTitle>
                      <CardDescription>Configure your branded AI portal</CardDescription>
                    </CardHeader>
                  </Card>
                </div>
              </section>
            </SubscriptionGate>

            {/* Enterprise Suite */}
            <SubscriptionGate requiredTier="dominate">
              <section className="space-y-6">
                <h2 className="text-3xl font-bold text-foreground">Enterprise Suite</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader>
                      <Settings className="w-10 h-10 text-primary mb-2" />
                      <CardTitle>White-Label Neil AI Platform</CardTitle>
                      <CardDescription>Fully branded AI platform for your business</CardDescription>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader>
                      <BarChart3 className="w-10 h-10 text-primary mb-2" />
                      <CardTitle>Advanced Analytics & Dashboard</CardTitle>
                      <CardDescription>Executive-level insights and reporting</CardDescription>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader>
                      <Bot className="w-10 h-10 text-primary mb-2" />
                      <CardTitle>Dedicated Account Manager</CardTitle>
                      <CardDescription>Personal support via Slack channel</CardDescription>
                    </CardHeader>
                  </Card>
                </div>
              </section>
            </SubscriptionGate>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
